import moment from 'moment'
import PropTypes from 'prop-types'
import { Fragment, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import InputTextField from 'components/common/InputTextField'
import Popover from 'components/common/Popover'
import PopoverMenu from 'components/common/PopoverMenu'
import Text, { colors, styles } from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import TitleWithIcon from 'components/common/TitleWithIcon'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import Icon from 'components/Icon'
import { ModalHeader, ModalSubtitle, ModalTitle } from 'components/Modal'
import NavIcon from 'components/navigation/Nav/NavIcon'
import TeamSelectionRow from 'components/rbac-settings/TeamSelectionRow'
import useAppRefresh from 'hooks/use-app-refresh'
import useAttackMetricsStatus from 'hooks/use-attack-metrics-status'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import theme from 'theme'
import { track } from 'track'
import { naturalSortBy } from 'utils/list'
import { pluralize } from 'utils/strings'
import { getUserDisplayName } from 'utils/user'

const UsersIcon = (props) => <Icon icon="users" {...props} />

function MenuSectionTitle(props) {
  return (
    <Text
      as="h5"
      color="normal"
      css={{ textTransform: 'uppercase' }}
      {...props}
    />
  )
}

function MenuSubSection(props) {
  return (
    <div
      css={{
        display: 'grid',
        gap: theme.space[3],
        paddingTop: theme.space[3],
        paddingRight: theme.space[2],
        paddingBottom: theme.space[3],
        paddingLeft: theme.space[2],
        borderBottom: theme.border.base,

        '&:first-of-type': {
          paddingTop: 0
        },

        '&:last-of-type': {
          paddingBottom: 0,
          borderBottom: 'none'
        }
      }}
      {...props}
    />
  )
}

function MenuItem({ to = '', ...rest }) {
  let AsComponent
  let linkProps = {}

  if (to) {
    const isInternalLink = /^\/(?!\/)/.test(to)
    if (isInternalLink) {
      AsComponent = Link
      linkProps.to = to
    } else {
      AsComponent = TrackedExternalLink
      linkProps.href = to
    }
  }

  return (
    <TextButton
      as={AsComponent}
      data-track-action="Menu Item Clicked"
      data-track-category="main-header-menu"
      data-track-on="click"
      {...linkProps}
      css={[
        styles.p,
        {
          color: colors.medium,
          textTransform: 'capitalize'
        }
      ]}
      {...rest}
    />
  )
}

MenuItem.propTypes = {
  to: PropTypes.string
}

const popoverWidth = 320

function MainHeaderMenuPopover({
  getPopoverProps,
  closePopover,
  showTeamSwitcher,
  setShowTeamSwitcher
}) {
  const {
    session,
    currentTeam,
    endSession,
    hasCompanyPrivilege,
    isRbacApiEnabled,
    hasElevatedCompanyRole,
    setActiveTeamId,
    companyDisplayName,
    isRMSubscription,
    RBAC,
    isTrial
  } = useAuth()

  const appRefresh = useAppRefresh()

  const { canUpdateCompanyAttackVisualizations } = useAttackMetricsStatus()

  const hasCompanySettingsLinks =
    hasCompanyPrivilege(RBAC.privileges.company.READ_ALL_USERS) ||
    (!isRMSubscription &&
      (hasCompanyPrivilege(RBAC.privileges.company.READ_ALL_USERS) ||
        hasCompanyPrivilege(RBAC.privileges.company.MANAGE_SAML) ||
        hasCompanyPrivilege(RBAC.privileges.company.MANAGE_COMPANY_PREFS) ||
        hasCompanyPrivilege(RBAC.privileges.company.MANAGE_INTEGRATIONS) ||
        hasCompanyPrivilege(RBAC.privileges.company.COMPANY_DEFAULT) ||
        canUpdateCompanyAttackVisualizations)) ||
    (!isRMSubscription &&
      hasCompanyPrivilege(RBAC.privileges.company.READ_ALL_USERS) &&
      isRbacApiEnabled) ||
    hasCompanyPrivilege(RBAC.privileges.company.MANAGE_COMPANY_PREFS) ||
    (isRMSubscription &&
      !isTrial &&
      hasCompanyPrivilege(RBAC.privileges.company.MODIFY_PAYMENT_METHOD))

  const onLogout = () => {
    endSession()

    // check to refresh the webapp when the user logs out
    appRefresh.doRefreshCheck()
  }

  const [searchInputValue, setSearchInputValue] = useState('')
  const menuContentRef = useRef(null)

  const teams = session?.teams ?? []
  const sortedTeams = naturalSortBy(
    teams.filter((t) =>
      t.name.toLowerCase().includes(searchInputValue.trim().toLowerCase())
    ),
    (t) => t.name
  )

  const teamCount = sortedTeams.length

  const switcherHeight = menuContentRef.current
    ? menuContentRef.current.offsetHeight
    : 100 // arbitrary small value to keep menu from getting too tall

  const getTeamSwitchRedirectUrl = (team) => {
    switch (true) {
      // Will remain on the team settings page if switching from that page.
      case Boolean(window.location.pathname.match(/^\/settings\/team\/[\w]+/)):
        return `/settings/team/${team.identifier}`
      // reports pages need to remain on the end point they came from
      case window.location.pathname.includes('/reports/'):
        return window.location.pathname
      // redirect to root feature page if switching teams
      // /scenarios/detail/[id] => /scenarios
      // /attacks/detail/[id] => /attacks
      // etc
      default:
        return `/${window.location.pathname
          .split('/') // split on /
          .filter(Boolean) // remove leading empty strings
          .slice(0, 1) // take first item
          .join('')}` // coerce into string
    }
  }

  return (
    <PopoverMenu
      css={{
        width: popoverWidth,
        padding: 0,
        paddingLeft: 0,
        paddingRight: 0,
        overflowX: 'hidden'
      }}
      getPopoverProps={getPopoverProps}
    >
      <div
        data-test-header-menu-content
        css={{
          display: 'grid',
          gridTemplateColumns: `${popoverWidth}px ${popoverWidth}px`,
          transform: `translateX(-${showTeamSwitcher ? popoverWidth : 0}px)`,
          transition: 'transform ease 300ms'
        }}
      >
        <div
          ref={menuContentRef}
          css={{
            width: popoverWidth,
            padding: theme.space[3]
          }}
        >
          <MenuSubSection css={{ gap: theme.space[2] }}>
            <Text as="h2" color="medium" css={{ wordBreak: 'break-all' }}>
              {getUserDisplayName(session)}
            </Text>
            <div css={{ display: 'flex', alignItems: 'center' }}>
              <Icon
                css={{
                  fontSize: theme.fontSizes[3],
                  color: theme.colors.steel[1],
                  marginRight: theme.space[1]
                }}
                icon="building"
              />
              <Text overflowEllipsis as="h4" color="normal">
                {companyDisplayName}
              </Text>
            </div>
            {session.last_login && (
              <Text as="small">
                Last Login:{' '}
                {moment(session.last_login).format('M/D/YYYY h:mm a')}
              </Text>
            )}
          </MenuSubSection>
          {currentTeam.identifier && (
            <MenuSubSection>
              <div
                css={{
                  display: 'grid',
                  gap: theme.space[2],
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'center'
                }}
              >
                <MenuSectionTitle>Current Team</MenuSectionTitle>
                {teamCount > 1 && (
                  <TextButton
                    data-no-popover-close
                    data-test-switch-team
                    data-track-action="open-switch-team-menu"
                    data-track-category="main-header-menu"
                    data-track-on="click"
                    onClick={() => setShowTeamSwitcher(true)}
                  >
                    Switch team
                  </TextButton>
                )}
              </div>
              <TitleWithIcon
                as="h4"
                data-test-active-team={currentTeam.name}
                icon={UsersIcon}
                label={currentTeam.name}
              />
              {!isRMSubscription && hasElevatedCompanyRole && (
                <MenuItem to="/settings/teams">Manage Teams</MenuItem>
              )}
            </MenuSubSection>
          )}
          <MenuSubSection>
            <MenuSectionTitle>Settings</MenuSectionTitle>
            {hasCompanySettingsLinks && (
              <MenuItem to="/settings">Company Settings</MenuItem>
            )}
            {session?.teams?.length > 0 && (
              <MenuItem to={`/settings/team/${currentTeam.identifier}`}>
                Team Settings
              </MenuItem>
            )}
            <MenuItem to="/profile">Account Settings</MenuItem>
          </MenuSubSection>
          {!isRMSubscription && (
            <MenuSubSection>
              <MenuSectionTitle>Help</MenuSectionTitle>
              <MenuItem to="https://www.gremlin.com/docs/infrastructure-layer/installation/">
                Installation Guides
              </MenuItem>
            </MenuSubSection>
          )}
          <MenuSubSection>
            <MenuItem data-test-log-out onClick={onLogout}>
              Log Out
            </MenuItem>
          </MenuSubSection>
        </div>
        <div
          css={{
            position: 'relative',
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
            width: popoverWidth,
            height: switcherHeight,
            padding: theme.space[3]
          }}
        >
          <div css={{ position: 'relative' }}>
            <ModalHeader>
              <TextButton
                data-no-popover-close
                css={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  fontWeight: theme.fontWeights[0]
                }}
                data-track-action="back-out-of-switch-team-menu"
                data-track-category="main-header-menu"
                data-track-on="click"
                onClick={() => setShowTeamSwitcher(false)}
              >
                <Icon
                  css={{
                    display: 'inline-block',
                    verticalAlign: 'middle'
                  }}
                  icon="chevronLeft"
                />
                <span
                  css={{
                    verticalAlign: 'middle'
                  }}
                >
                  Back
                </span>
              </TextButton>
              <ModalTitle>Switch Team</ModalTitle>
              <ModalSubtitle>
                <Icon
                  css={{
                    height: theme.space[3],
                    width: theme.space[3],
                    marginRight: theme.space[1]
                  }}
                  icon="users"
                />
                <span>{currentTeam.name}</span>
              </ModalSubtitle>
            </ModalHeader>
            <InputTextField
              data-test-team-switcher-input
              css={{
                marginTop: theme.space[2],
                marginBottom: theme.space[3]
              }}
              data-track-action="focus-team-search-input"
              data-track-category="main-header-menu"
              data-track-on="click"
              name="team-search"
              placeholder={`Search ${teamCount} ${pluralize(
                'team',
                teamCount
              )}`}
              type="text"
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
            />
          </div>

          <div
            css={{
              overflowY: 'scroll',
              overflowX: 'hidden',
              border: theme.border.base,
              borderRadius: theme.borderRadius[0]
            }}
            tabIndex="0"
          >
            {sortedTeams.map((team) => {
              return (
                <TeamSelectionRow
                  key={team.identifier}
                  data-track-on="click"
                  multiSelect={false}
                  showId={false}
                  team={team}
                  onTeamClick={async (team) => {
                    await setActiveTeamId(team.identifier)

                    track('Team Selected', {
                      category: 'MainHeaderMenu'
                    })

                    window.location.href = getTeamSwitchRedirectUrl(team)

                    closePopover()

                    toast({
                      type: 'success',
                      text: `Successfully switched to team: ${team.name}`,
                      testKey: team.name
                    })

                    setShowTeamSwitcher(false)
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>
    </PopoverMenu>
  )
}

MainHeaderMenuPopover.propTypes = {
  closePopover: PropTypes.func,
  getPopoverProps: PropTypes.func,
  showTeamSwitcher: PropTypes.bool,
  setShowTeamSwitcher: PropTypes.func
}

export default function MainHeaderMenu() {
  const [isTeamSwitcherVisible, setIsTeamSwitcherVisible] = useState(false)

  return (
    <Fragment>
      <Popover
        edgeBuffer={theme.space[3]}
        ignoreOutsideClickElementAttr="data-no-popover-close"
        openOnClick={true}
        onClose={() => setIsTeamSwitcherVisible(false)}
        onOpen={() => {
          track('Main Header Menu Opened', {
            category: 'MainHeaderMenu'
          })
        }}
      >
        {({ getTargetProps, getPopoverProps, open, closePopover }) => {
          return (
            <Fragment>
              <div data-test-header-menu {...getTargetProps()}>
                <NavIcon open={open}>
                  <Icon
                    css={{
                      fontSize: theme.fontSizes[3]
                    }}
                    icon="users"
                  />
                </NavIcon>
              </div>
              <MainHeaderMenuPopover
                closePopover={closePopover}
                getPopoverProps={getPopoverProps}
                setShowTeamSwitcher={setIsTeamSwitcherVisible}
                showTeamSwitcher={isTeamSwitcherVisible}
              />
            </Fragment>
          )
        }}
      </Popover>
    </Fragment>
  )
}
